import { excelSerialToDate, isDateCell } from '@/lib/excel';
import { toast } from '@/hooks/use-toast';

export const HEADERS = {
  MA_DV: 'Mã dịch vụ*',
  TEN_DV: 'Tên dịch vụ*',
  TEN_KHAC: 'Tên khác',
  DVT: 'Đvt',
  TK_DOANH_THU: 'Tk doanh thu',
  TK_CHI_PHI: 'Tk chi phí',
  MA_THUE: 'Mã thuế',
  GIA_BAN: 'Giá bán',
  GIA_MUA: 'Giá mua',
  MA_PHI: 'Mã phí'
} as const;

type ColumnKey =
  | 'ma_dv'
  | 'ten_dv'
  | 'ten_khac'
  | 'dvt'
  | 'tk_dt'
  | 'tk_cp'
  | 'ma_thue'
  | 'gia_ban'
  | 'gia_mua'
  | 'ma_phi';

interface ColumnMapping {
  key: ColumnKey;
  index: number;
  header: string;
  required?: boolean;
}

interface ParsedExcelData {
  mainData: Record<string, unknown[]>;
}

interface ParsedServiceRow {
  ma_dv: string;
  ten_dv: string;
  ten_khac?: string | null;
  dvt?: string | null;
  tk_dt?: string | null;
  tk_cp?: string | null;
  ma_thue?: string | null;
  gia_ban?: number | null;
  gia_mua?: number | null;
  ma_phi?: string | null;
}

export interface ImportBody {
  ma_dv: string;
  ten_dv: string;
  ten_dv2?: string | null;
  dvt?: string | null;
  tk_dt?: string | null;
  tk_cp?: string | null;
  ma_thue?: string | null;
  gia_nt2: number;
  gia_nt: number;
  ma_cp?: string | null;
  ma_phi?: string | null;
  ma_bp?: string | null;
  ma_hd?: string | null;
  ma_ku?: string | null;
  ma_vv?: string | null;
  status: number;
}

export interface ValidationError {
  row: number;
  field: ColumnKey;
  fieldName: string;
  message: string;
}

export interface ImportErrorData {
  errors: ValidationError[];
  columnMapping: Record<string, ColumnMapping>;
}

const HEADER_CONFIG: Array<{ header: string; key: ColumnKey; required?: boolean }> = [
  { header: HEADERS.MA_DV, key: 'ma_dv', required: true },
  { header: HEADERS.TEN_DV, key: 'ten_dv', required: true },
  { header: HEADERS.TEN_KHAC, key: 'ten_khac' },
  { header: HEADERS.DVT, key: 'dvt' },
  { header: HEADERS.TK_DOANH_THU, key: 'tk_dt' },
  { header: HEADERS.TK_CHI_PHI, key: 'tk_cp' },
  { header: HEADERS.MA_THUE, key: 'ma_thue' },
  { header: HEADERS.GIA_BAN, key: 'gia_ban' },
  { header: HEADERS.GIA_MUA, key: 'gia_mua' },
  { header: HEADERS.MA_PHI, key: 'ma_phi' }
];

const FIELD_LABELS: Record<ColumnKey, string> = {
  ma_dv: 'Mã dịch vụ',
  ten_dv: 'Tên dịch vụ',
  ten_khac: 'Tên khác',
  dvt: 'Đvt',
  tk_dt: 'Tk doanh thu',
  tk_cp: 'Tk chi phí',
  ma_thue: 'Mã thuế',
  gia_ban: 'Giá bán',
  gia_mua: 'Giá mua',
  ma_phi: 'Mã phí'
};

const REQUIRED_KEYS: ColumnKey[] = ['ma_dv', 'ten_dv'];

const normalizeHeader = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\*/g, '')
    .replace(/\s+/g, '')
    .toLowerCase();

const createColumnMapping = (headers: unknown[]): Record<string, ColumnMapping> => {
  const mapping: Record<string, ColumnMapping> = {};

  headers.forEach((header, index) => {
    const cleanHeader = String(header ?? '').trim();
    if (!cleanHeader || cleanHeader === '*') return;

    const normalizedHeader = normalizeHeader(cleanHeader);
    const config = HEADER_CONFIG.find(col => normalizeHeader(col.header) === normalizedHeader);
    if (!config) return;

    const nextHeader = headers[index + 1];
    let resolvedHeader = cleanHeader;
    if (
      nextHeader &&
      String(nextHeader).trim() === '*' &&
      config.header.includes('*') &&
      !resolvedHeader.includes('*')
    ) {
      resolvedHeader = `${resolvedHeader}*`;
    } else if (config.header.includes('*') && !resolvedHeader.includes('*')) {
      resolvedHeader = config.header;
    }

    mapping[`${resolvedHeader}_${index}`] = {
      key: config.key,
      index,
      header: resolvedHeader,
      required: config.required
    };
  });

  return mapping;
};

const readExcelFile = async (file: File): Promise<{ headers: unknown[]; dataRows: unknown[][] }> => {
  const XLSX = await import('xlsx');
  const arrayBuffer = await file.arrayBuffer();
  const workbook = XLSX.read(arrayBuffer, { type: 'array' });
  const firstSheetName = workbook.SheetNames[0];
  if (!firstSheetName) {
    throw new Error('Không tìm thấy worksheet');
  }

  const worksheet = workbook.Sheets[firstSheetName];
  const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
  const headers: unknown[] = [];
  const dataRows: unknown[][] = [];

  for (let columnIndex = range.s.c; columnIndex <= range.e.c; columnIndex++) {
    const cellAddress = XLSX.utils.encode_cell({ r: range.s.r, c: columnIndex });
    const cellObj = worksheet[cellAddress];
    headers.push(cellObj ? cellObj.v : '');
  }

  for (let rowIndex = range.s.r + 1; rowIndex <= range.e.r; rowIndex++) {
    const row: unknown[] = [];
    let hasData = false;

    for (let columnIndex = range.s.c; columnIndex <= range.e.c; columnIndex++) {
      const cellAddress = XLSX.utils.encode_cell({ r: rowIndex, c: columnIndex });
      const cellObj = worksheet[cellAddress];

      if (!cellObj) {
        row.push('');
        continue;
      }

      if (isDateCell(cellObj)) {
        row.push(excelSerialToDate(cellObj.v));
        hasData = true;
      } else {
        row.push(cellObj.v ?? '');
        if (String(cellObj.v ?? '').trim() !== '') hasData = true;
      }
    }

    if (hasData) {
      dataRows.push(row);
    }
  }

  return { headers, dataRows };
};

const parseExcelData = (columnMapping: Record<string, ColumnMapping>, dataRows: unknown[][]): ParsedExcelData => {
  const mainData: Record<string, unknown[]> = {};

  Object.values(columnMapping).forEach(({ key, index }) => {
    mainData[key] = dataRows.map(row => row[index]);
  });

  return { mainData };
};

const toStringValue = (value: unknown): string => {
  if (value === undefined || value === null) return '';
  return String(value).trim();
};

const toNullableString = (value: unknown): string | null => {
  const str = toStringValue(value);
  return str === '' ? null : str;
};

const toNumberValue = (value: unknown): number => {
  if (value === undefined || value === null || String(value).trim() === '') {
    return 0;
  }
  const normalized = String(value).replace(/,/g, '.').trim();
  const parsed = Number(normalized);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const transformToRows = (parsedData: ParsedExcelData): ParsedServiceRow[] => {
  const { mainData } = parsedData;
  const rowCount = Object.values(mainData).reduce((max, values) => Math.max(max, values.length), 0);
  const rows: ParsedServiceRow[] = [];

  for (let index = 0; index < rowCount; index++) {
    rows.push({
      ma_dv: toStringValue(mainData.ma_dv?.[index] ?? ''),
      ten_dv: toStringValue(mainData.ten_dv?.[index] ?? ''),
      ten_khac: toNullableString(mainData.ten_khac?.[index]),
      dvt: toNullableString(mainData.dvt?.[index]),
      tk_dt: toNullableString(mainData.tk_dt?.[index]),
      tk_cp: toNullableString(mainData.tk_cp?.[index]),
      ma_thue: toNullableString(mainData.ma_thue?.[index]),
      gia_ban: toNumberValue(mainData.gia_ban?.[index]),
      gia_mua: toNumberValue(mainData.gia_mua?.[index]),
      ma_phi: toNullableString(mainData.ma_phi?.[index])
    });
  }

  return rows;
};

const buildUUIDPayload = (rows: ParsedServiceRow[]) => ({
  main: {
    dvt: rows.map(row => row.dvt || null),
    tk_dt: rows.map(row => row.tk_dt || null),
    tk_cp: rows.map(row => row.tk_cp || null),
    ma_thue: rows.map(row => row.ma_thue || null),
    ma_phi: rows.map(row => row.ma_phi || null)
  }
});

type ResolvableField = 'dvt' | 'tk_dt' | 'tk_cp' | 'ma_thue' | 'ma_phi';

const createValidationError = (row: number, field: ColumnKey, message?: string): ValidationError => ({
  row,
  field,
  fieldName: FIELD_LABELS[field],
  message: message || `${FIELD_LABELS[field]} không hợp lệ`
});

const resolveReferences = async (
  rows: ParsedServiceRow[],
  getUUIDByCode: UUIDLookup
): Promise<{ bodies: ImportBody[]; errors: ValidationError[] }> => {
  const errors: ValidationError[] = [];
  const bodies: ImportBody[] = rows.map(row => ({
    ma_dv: row.ma_dv,
    ten_dv: row.ten_dv,
    ten_dv2: row.ten_khac || null,
    dvt: row.dvt || null,
    tk_dt: row.tk_dt || null,
    tk_cp: row.tk_cp || null,
    ma_thue: row.ma_thue || null,
    gia_nt2: row.gia_ban ?? 0,
    gia_nt: row.gia_mua ?? 0,
    ma_cp: null,
    ma_phi: row.ma_phi || null,
    ma_bp: null,
    ma_hd: null,
    ma_ku: null,
    ma_vv: null,
    status: 1
  }));

  const uuidPayload = buildUUIDPayload(rows);
  const hasUUIDRequest = Object.values(uuidPayload.main).some(list => list.some(code => Boolean(code)));

  if (hasUUIDRequest) {
    try {
      const response = await getUUIDByCode(uuidPayload);
      const mainResponse = (response as UUIDLookupResponse | null)?.main || {};

      const applyResolvedField = (responseKey: ResolvableField, field: ResolvableField, emptyMessage: string) => {
        const resolved = (mainResponse?.[responseKey] as (string | null)[]) || [];
        bodies.forEach((body, index) => {
          const originalValue = rows[index][field];
          if (!originalValue) {
            body[field] = null;
            return;
          }

          const uuid = resolved[index];
          if (!uuid) {
            errors.push(createValidationError(index + 1, field, emptyMessage));
          }
          body[field] = uuid ?? null;
        });
      };

      applyResolvedField('dvt', 'dvt', 'Không tìm thấy đơn vị tính');
      applyResolvedField('tk_dt', 'tk_dt', 'Không tìm thấy Tk doanh thu');
      applyResolvedField('tk_cp', 'tk_cp', 'Không tìm thấy Tk chi phí');
      applyResolvedField('ma_thue', 'ma_thue', 'Không tìm thấy mã thuế');
      applyResolvedField('ma_phi', 'ma_phi', 'Không tìm thấy mã phí');
    } catch (error) {
      console.error('Không thể tra cứu mã tham chiếu dịch vụ từ Excel import', error);
      errors.push(createValidationError(1, 'ma_dv', 'Không thể tra cứu mã tham chiếu'));
    }
  }

  return { bodies, errors };
};

const isEmpty = (value: unknown) => value === undefined || value === null || String(value).trim() === '';

const validateRequiredFields = (
  columnMapping: Record<string, ColumnMapping>,
  dataRows: unknown[][]
): ValidationError[] => {
  if (!dataRows.length) return [];

  const errors: ValidationError[] = [];
  const requiredColumns = Object.values(columnMapping).filter(column => column.required);

  requiredColumns.forEach(column => {
    dataRows.forEach((row, rowIndex) => {
      const cellValue = row[column.index];
      if (isEmpty(cellValue)) {
        errors.push(createValidationError(rowIndex + 1, column.key, `${FIELD_LABELS[column.key]} không được bỏ trống`));
      }
    });
  });

  return errors;
};

const formatValidationErrors = (errors: ValidationError[]): string => {
  if (!errors.length) return '';

  const grouped = errors.reduce<Record<string, string[]>>((acc, error) => {
    const key = String(error.row);
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(error.message);
    return acc;
  }, {});

  return Object.entries(grouped)
    .map(([row, messages]) => `Dòng ${row}: ${messages.join(', ')}`)
    .join('\n');
};

export type UUIDLookup = (payload: unknown) => Promise<unknown>;
type UUIDLookupResponse = { main?: Record<string, Array<string | null | undefined>> };

export const handleImportExcel = async (
  getUUIDByCode: UUIDLookup,
  onSubmit: (bodies: ImportBody[]) => Promise<void>,
  onError?: (errorData: ImportErrorData) => void
): Promise<void> => {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = '.xlsx,.xls';

  input.onchange = async (event: Event) => {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];
    if (!file) return;

    try {
      const { headers, dataRows } = await readExcelFile(file);
      if (dataRows.length === 0) {
        toast({
          description: 'File Excel không có dữ liệu',
          variant: 'destructive'
        });
        return;
      }

      const columnMapping = createColumnMapping(headers);
      const availableKeys = new Set(Object.values(columnMapping).map(column => column.key));
      const missingRequiredHeaders = REQUIRED_KEYS.filter(key => !availableKeys.has(key));

      if (missingRequiredHeaders.length > 0) {
        toast({
          description: 'Không tìm thấy đủ cột bắt buộc trong file Excel.',
          variant: 'destructive'
        });
        return;
      }

      const parsedData = parseExcelData(columnMapping, dataRows);
      const requiredErrors = validateRequiredFields(columnMapping, dataRows);
      if (requiredErrors.length > 0) {
        if (onError) {
          onError({ errors: requiredErrors, columnMapping });
        } else {
          toast({
            description: formatValidationErrors(requiredErrors),
            variant: 'destructive'
          });
        }
        return;
      }

      const rows = transformToRows(parsedData);
      const { bodies, errors } = await resolveReferences(rows, getUUIDByCode);
      if (errors.length > 0) {
        if (onError) {
          onError({ errors, columnMapping });
        } else {
          toast({
            description: formatValidationErrors(errors),
            variant: 'destructive'
          });
        }
        return;
      }

      await onSubmit(bodies);
      toast({
        description: `Đã nhập ${bodies.length} dịch vụ từ Excel`,
        variant: 'default'
      });
    } catch (error) {
      console.error('Lỗi khi nhập dữ liệu dịch vụ từ Excel:', error);
      toast({
        description: error instanceof Error ? error.message : 'Có lỗi xảy ra khi nhập dữ liệu Excel',
        variant: 'destructive'
      });
    } finally {
      target.value = '';
    }
  };

  input.click();
};
